import {
  format,
  formatDuration,
  intervalToDuration,
  parse,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks
} from 'date-fns';

import * as dateFormats from 'formats/date';

export function formatToShortDate(datetime) {

  return format(datetime, dateFormats.SHORT_DATE);

}

export function tryFormatToShortDate(datetime) {

  if (datetime === null || datetime === '' || datetime === void 0) {
    return '';
  }

  return formatToShortDate(datetime);

}

export function formatBrowserDateToShortDate(browserDateString) {

  return formatToShortDate((new Date(browserDateString)).getTime());

}

export function formatToLongDate(datetime) {

  return format(datetime, dateFormats.LONG_DATE);

}

export function formatToCompactLongDate(datetime) {

  return format(datetime, dateFormats.COMPACT_LONG_DATE);

}

export function tryFormatToCompactLongDate(datetime) {

  if (datetime === null || datetime === '' || datetime === void 0) {
    return '';
  }

  return formatToCompactLongDate(datetime);

}

export function formatToLongDateParts(datetime) {

  return {
    date: format(datetime, dateFormats.LONG_DATE_DATE),
    time: format(datetime, dateFormats.LONG_DATE_TIME)
  };

}

export function formatToCompactDate(datetime) {

  return format(datetime, dateFormats.COMPACT_LONG_DATE);

}

export function formatToCompactDateV2(datetime) {

  return format(datetime, dateFormats.COMPACT_DATE_V2);

}

export function formatToCompactShortDate(datetime) {

  return format(datetime, dateFormats.COMPACT_SHORT_DATE);

}

export function tryFormatToCompactShortDate(datetime) {

  if (datetime === null || datetime === '' || datetime === void 0) {
    return '';
  }

  return formatToCompactShortDate(datetime);

}

// A timestamp of 0 is not midnight in 1970 — it is a field nobody ever set. A contact imported from a
// discovery has never been on the site, so it has no first-seen or last-seen time at all, and the epoch
// a zeroed int64 serializes as must never be drawn as a date.
export function hasDateTime(datetime) {

  if (datetime === null || datetime === void 0 || datetime === '') {
    return false;
  }

  return datetime !== 0;

}

export function formatToCompactDateParts(datetime) {

  return {
    date: format(datetime, dateFormats.COMPACT_DATE_DATE),
    time: format(datetime, dateFormats.COMPACT_DATE_TIME)
  };

}

export function formatToServerDateFormat(date) {

  return format(date, dateFormats.server.DATE);

}

export function extractDayAndMonthFromDate(date) {

  const _date = new Date(date);

  const month = format(_date, 'MMM');
  const day = format(_date, 'd');

  return { month, day };

}

export function formatDateTimeToApplicationDateTime(datetime) {

  return format(new Date(datetime), dateFormats.application.DATE_TIME);

}

export function formatBrowserTimeToApplicationTime(time) {

  if (time === '') {
    return '';
  }

  const timestamp = `December 1, 2022 ${time}:00`; // sample date is added to create timestamp

  const date = new Date(timestamp);

  return format(date, dateFormats.application.TIME);

}

export function formatApplicationTimeToBrowserTime(timeString) {

  if (timeString === '' || timeString === null) {
    return '';
  }

  return format(parse(timeString, dateFormats.application.TIME, new Date()), dateFormats.browser.TIME);

}

export const convertTimeTo12HoursFormat = formatBrowserTimeToApplicationTime;

export function formatApplicationDateToBrowserDate(date) {

  if (date === '') {
    return '';
  }

  return format(parse(date, dateFormats.application.DATE, new Date()), dateFormats.browser.DATE);

}

export function formatDateObjectToBrowserDate(date) {

  return format(date, dateFormats.browser.DATE);

}

export function formatDateObjectToBrowserTime(date) {

  return format(date, dateFormats.browser.TIME);

}

export function formatDateObjectToBrowserDateTime(date) {

  return format(date, dateFormats.browser.DATE_TIME);

}

export function formatDateObjectToApplicationDate(date) {

  return format(date, dateFormats.application.DATE);

}

export function tryFormatToBrowserFormat(datetime) {

  if (datetime === '' || datetime === null || datetime === void 0) {
    return '';
  }

  return formatDateObjectToBrowserDateTime(new Date(+datetime));

}

export function parseBrowserDateAndTimeToDate(dateString, timeString) {

  return parse(`${dateString} ${timeString}`, dateFormats.browser.DATE_TIME, new Date());

}

export function formatDateObjectToApplicationTime(date) {

  return format(date, dateFormats.application.TIME);

}

export function getMinimumStartTime(timestamp) {

  return format(timestamp, 'HH:mm');

}

export function formatBrowserDateToApplicationDate(date) {

  if (date === '') {
    return '';
  }

  return formatDateObjectToApplicationDate(new Date(date));

}

export function toCountDownFormat(startDateTime, endDateTime) {

  if (startDateTime <= endDateTime) {
    return '0 seconds';
  }

  let duration = intervalToDuration({
    start: startDateTime,
    end: endDateTime
  });

  return formatDuration(duration, {
    delimiter: ', '
  });

}

export function diffInMinutes(laterDate, earlierDate) {

  return differenceInMinutes(laterDate, earlierDate);

}

export function diffInHours(laterDate, earlierDate) {

  return differenceInHours(laterDate, earlierDate);

}

export function diffInDays(laterDate, earlierDate) {

  return differenceInDays(laterDate, earlierDate);

}

export function diffInWeeks(laterDate, earlierDate) {

  return differenceInWeeks(laterDate, earlierDate);

}

export { toFuzzyTime } from 'utilities/fuzzy-time';

export function humanReadableDuration(milliseconds) {
  let remainingMilliseconds = milliseconds;
  const secondsInMillisecond = 1000;
  const minutesInMillisecond = secondsInMillisecond * 60;
  const hoursInMillisecond = minutesInMillisecond * 60;

  const hours = Math.floor(remainingMilliseconds / hoursInMillisecond);
  remainingMilliseconds %= hoursInMillisecond;

  const minutes = Math.floor(remainingMilliseconds / minutesInMillisecond);
  remainingMilliseconds %= minutesInMillisecond;

  const seconds = Math.floor(remainingMilliseconds / secondsInMillisecond);

  let result = '';

  if (hours > 0) {
    result += `${hours} hrs `;
  }

  if (minutes > 0) {
    result += `${minutes} mins `;
  }

  if (seconds > 0) {
    result += `${seconds} secs`;
  }

  return result.trim();
}